<script setup lang="ts">
    import { reactive, ref, computed, inject, onMounted, watch } from 'vue';
    import BSToggleButtons from '@shared/components/BSToggleButtons.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import Modal from '@shared/components/Modal.vue';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    const props = defineProps<{
        serviceSubscriptionId: string;
        show: boolean;
    }>();

    const emits = defineEmits<{
        (e: 'close'): void;
        (e: 'serviceSubscriptionServiceLevelUpdated'): void;
    }>();

    const state = reactive({
        selectedServiceLevelPriceId: null as string | null,
        announceUpgrade: true as boolean,
        show: props.show,
    });

    const subscriptionFilters = reactive({
        name: 'Basic' as string,
        currency: 'SEK' as string,
        version: 'All' as string,
    });

    const subscriptionTypes = [
        { name: 'Basic', value: 'Basic' },
        { name: 'Pro', value: 'Pro' },
        { name: 'Automation', value: 'Automation' },
        { name: 'Enterprise', value: 'Enterprise' },
    ];

    const currencies = [
        { name: 'SEK', value: 'SEK' },
        { name: 'EUR', value: 'EUR' },
        { name: 'USD', value: 'USD' },
        { name: 'NOK', value: 'NOK' },
        { name: 'DKK', value: 'DKK' },
    ];

    const versions = [
        { name: 'All', value: 'All' },
        { name: 'v1', value: '1' },
        { name: 'v2', value: '2' },
        { name: 'v3', value: '3' },
        { name: 'v4', value: '4' },
    ];

    const filteredSubscriptionTierServiceLevelOptions = computed(() => {
        return filteredSubscriptionTierServiceLevels.value.map((subscription) => ({
            label: subscription.Name,
            value: subscription.ServiceLevelPriceId,
        }));
    });

    const filteredSubscriptionTierServiceLevels = computed(() => {
        return (
            subscriptionAccess.state.subscriptionTierServiceLevels?.filter((subscription) => {
                const matchesName = subscription.ServiceSubscriptionTier.Name === subscriptionFilters.name;
                const matchesCurrency = subscription.Currency.CurrencyCode === subscriptionFilters.currency;
                const matchesVersion =
                    subscriptionFilters.version == 'All' || subscription.ServiceSubscriptionTier.Version.toString() === subscriptionFilters.version;
                return matchesName && matchesCurrency && matchesVersion;
            }) || []
        );
    });

    function hasSubscriptionsForVersion(versionValue: string) {
        return (subscriptionAccess.state.subscriptionTierServiceLevels || []).some((subscription) => {
            const matchesName = subscription.ServiceSubscriptionTier.Name === subscriptionFilters.name;
            const matchesCurrency = subscription.Currency.CurrencyCode === subscriptionFilters.currency;
            const matchesVersion = versionValue === 'All' || subscription.ServiceSubscriptionTier.Version.toString() === versionValue;
            return matchesName && matchesCurrency && matchesVersion;
        });
    }

    const availableVersions = computed(() => versions.filter((version) => version.value === 'All' || hasSubscriptionsForVersion(version.value)));

    function selectHighestAvailableVersion() {
        const versionValues = versions.filter((version) => version.value !== 'All').map((version) => version.value);
        const highestAvailableVersion = [...versionValues].reverse().find((version) => hasSubscriptionsForVersion(version));
        subscriptionFilters.version = highestAvailableVersion ?? 'All';
    }

    const isInitializing = ref(true);

    watch(
        () => [subscriptionFilters.name, subscriptionFilters.currency],
        () => {
            if (!isInitializing.value) {
                selectHighestAvailableVersion();
            }
        }
    );

    onMounted(async () => {
        state.show = props.show;
        await get();
    });

    watch(
        () => props.show,
        async (show: boolean) => {
            console.log('show', show);
            state.show = show;
            await get();
        }
    );

    async function get() {
        if (state.show) {
            isInitializing.value = true;

            await subscriptionAccess.getSubscriptionTierServiceLevels().then(() => {
                state.selectedServiceLevelPriceId = subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.ServiceLevelPriceId;

                subscriptionFilters.name = subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.ServiceSubscriptionTier.Name;
                subscriptionFilters.currency = subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.Currency.CurrencyCode;
                subscriptionFilters.version = subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.ServiceSubscriptionTier.Version.toString();
            });

            isInitializing.value = false;
        }
    }

    function closeChangeSubscriptionModal() {
        state.show = false;
        emits('close');
    }

    async function updateServiceSubscriptionServiceLevel() {
        if (state.selectedServiceLevelPriceId) {
            await subscriptionAccess
                .updateServiceSubscriptionServiceLevel(props.serviceSubscriptionId, state.selectedServiceLevelPriceId, state.announceUpgrade)
                .then(async () => {
                    state.selectedServiceLevelPriceId = null;
                    closeChangeSubscriptionModal();

                    emits('serviceSubscriptionServiceLevelUpdated');
                });
        }
    }
</script>

<template>
    <Modal size="lg" :show="state.show" @close="closeChangeSubscriptionModal" title="Change subscription">
        <template #content>
            <Loading
                :show="subscriptionAccess.state.updatingServiceLevel || subscriptionAccess.state.loadingSubscriptionTierServiceLevels"
                loadingStyle="progress-bar"
                :fixed="true" />

            <BsAlert
                :type="AlertTypes.Warning"
                :dismissible="false"
                title="OBS!"
                content="Om du ändrar valuta på abonnemanget, se till att ändra månadskostnad för eventuella tillägg/addons." />

            <p v-if="subscriptionAccess.state.boServiceSubscription">
                Current subscription:
                {{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.ServiceSubscriptionTier.Name }}
                {{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.ServiceLevel.Name }}
                ({{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.MonthlyPrice }}
                {{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode }}) (version:
                {{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.ServiceSubscriptionTier.Version }})
            </p>

            <h3 class="h4">Filters</h3>
            <div class="d-flex gap-lg flex-wrap m-lg-b">
                <BSToggleButtons v-model:value="subscriptionFilters.name" :options="subscriptionTypes" buttonClass="btn-sm" />
                <BSToggleButtons v-model:value="subscriptionFilters.currency" :options="currencies" buttonClass="btn-sm" />
                <BSToggleButtons v-model:value="subscriptionFilters.version" :options="availableVersions" buttonClass="btn-sm" />
            </div>

            <template v-if="!subscriptionAccess.state.loadingSubscriptionTierServiceLevels">
                <FormSelect
                    class="form-group"
                    label="Select subscription"
                    :options="filteredSubscriptionTierServiceLevelOptions"
                    :modelValue="state.selectedServiceLevelPriceId"
                    @update:modelValue="(newVal) => (state.selectedServiceLevelPriceId = newVal)" />

                <BsAlert
                    v-if="filteredSubscriptionTierServiceLevels.length === 0"
                    :type="AlertTypes.Warning"
                    :dismissible="false"
                    title="Oops!"
                    content="Didn't find any matching subscriptions." />
            </template>

            <div class="form-group">
                <FormCheckable
                    :modelValue="state.announceUpgrade"
                    :checked="state.announceUpgrade"
                    label="Announce upgrade in Slack"
                    :switchStyle="SwitchStyle.Standard"
                    @update:modelValue="(newVal) => (state.announceUpgrade = newVal)" />
            </div>
        </template>

        <template #button>
            <button
                type="button"
                class="btn btn-primary"
                @click="updateServiceSubscriptionServiceLevel"
                :disabled="
                    state.selectedServiceLevelPriceId == null ||
                    !filteredSubscriptionTierServiceLevels.some((subscription) => subscription.ServiceLevelPriceId === state.selectedServiceLevelPriceId) ||
                    subscriptionAccess.state.updatingServiceLevel ||
                    state.selectedServiceLevelPriceId == subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.ServiceLevelPriceId
                ">
                Update subscription
            </button>
        </template>
    </Modal>
</template>
